export const isTermInRange = (selectedTerm, termCodeEffective, endTerm) => {
    if (!selectedTerm) return true;
    if (!termCodeEffective && !endTerm) return true;
    const afterStart = !termCodeEffective || termCodeEffective <= selectedTerm;
    const beforeEnd = !endTerm || selectedTerm <= endTerm;
    return afterStart && beforeEnd;
};

export const isNumericTerm = (term) => /^\d+$/.test(term);

export const filterByTermRange = (arr, selectedTerm) => {
    if (!selectedTerm || !arr?.length) return arr;

    if (!isNumericTerm(selectedTerm)) return arr;

    const hasNonNumericBounds = arr.some(entry =>
        (entry.termCodeEffective && !isNumericTerm(entry.termCodeEffective)) ||
        (entry.endTerm && !isNumericTerm(entry.endTerm))
    );
    if (hasNonNumericBounds) return arr;

    return arr.filter(entry => isTermInRange(selectedTerm, entry.termCodeEffective, entry.endTerm));
};

const isMatch = (selectedValue, ruleValue, allowBlank = false) => (
    !selectedValue ||
    (allowBlank && !ruleValue) ||
    selectedValue === ruleValue
);

const getArrMatch = (selectedValue, arr, codeField) => {
    let match = true;
    let validRecords = arr || [];

    if (selectedValue && arr?.length) {
        validRecords = arr.filter(
            v => isMatch(selectedValue, v?.[codeField])
        );
        match = !!validRecords.length;
    }

    return { match, validRecords };
};

const getConcentrationMatch = (concentration, concentrations, validMajors) => {
    let match = true;
    let validRecords = concentrations || [];

    if (concentrations?.length) {
        validRecords = concentrations.filter(c => (
            !c.concentrationMajorRule || validMajors.some(
                md => md.majorRule === c.concentrationMajorRule
            )
        ));
        match = getArrMatch(
            concentration, validRecords, 'concentrationCode'
        ).match;
    }

    return { match, validRecords };
};

export const filterCurriculumValues = (selectedValues, rules) => {
    const {
        program: selectedProgram,
        level: selectedLevel,
        major: selectedMajor,
        location: selectedLocation,
        minor: selectedMinor,
        concentration: selectedConcentration,
        term: selectedTerm
    } = selectedValues;
    // Codes to use if the field on the app has a value
    // Or, in the case of location/major/minor/concentration,
    // if any eligible rule is missing the field.
    const {
        eligibleRules, ...fallbackValues
    } = rules.reduce((codesByField, rule) => {
        const levelMatch = isMatch(selectedLevel, rule.levelCode);

        if (rule.levelCode) {
            codesByField.levels.add(rule.levelCode);
        }

        // Fall back to any codes on rules that match the level
        if (levelMatch) {
            const programMatch = isMatch(selectedProgram, rule.programCode);
            const locationMatch = isMatch(selectedLocation, rule.campusCode, true);
            const termFilteredMajors = filterByTermRange(rule.majorsDepartments, selectedTerm);
            const termFilteredMinors = filterByTermRange(rule.minors, selectedTerm);
            const termFilteredConcentrations = filterByTermRange(rule.concentrations, selectedTerm);
            const {
                match: majorMatch,
                validRecords: validMajors
            } = getArrMatch(selectedMajor, termFilteredMajors, 'majorCode');

            if (rule.programCode) {
                codesByField.programs.add(rule.programCode);
            }

            if (!rule.campusCode) {
                codesByField.locations = null;
            } else if (codesByField.locations) {
                codesByField.locations.add(rule.campusCode);
            }

            termFilteredMajors?.forEach(md => {
                if (md.webDescription && md.webDisplayInd) {
                    codesByField.majorDescriptions.push({
                        major: md.majorCode,
                        program: rule.programCode,
                        label: md.webDescription,
                        value: md.majorRule
                    });
                }
            });

            // Consider program/location when falling back with major/minor/concentration
            if (programMatch && locationMatch) {
                if (!termFilteredMajors?.length) {
                    codesByField.majors = null;
                } else if (codesByField.majors) {
                    termFilteredMajors?.forEach(md => {
                        if (md.majorCode) {
                            codesByField.majors.add(md.majorCode);
                        }
                    });
                }

                if (!termFilteredMinors?.length) {
                    codesByField.minors = null;
                } else if (codesByField.minors) {
                    termFilteredMinors?.forEach(m => {
                        if (m.minorCode) {
                            codesByField.minors.add(m.minorCode);
                        }
                    });
                }

                if (majorMatch) {
                    const { match: minorMatch } = getArrMatch(
                        selectedMinor, termFilteredMinors, 'minorCode'
                    );
                    const {
                        match: concentrationMatch,
                        validRecords: validConcentrations
                    } = getConcentrationMatch(
                        selectedConcentration, termFilteredConcentrations, validMajors
                    );

                    if (codesByField.concentrations) {
                        if (concentrationMatch && !validConcentrations.length) {
                            // ALL concentrations are valid
                            codesByField.concentrations = null;
                        } else {
                            // Always show all concentrations for the rule/major.
                            validConcentrations.forEach(c => {
                                codesByField.concentrations.add(c.concentrationCode);
                            });
                        }
                    }

                    if (minorMatch && concentrationMatch) {
                        codesByField.eligibleRules.push(rule);
                    }
                }
            }
        }

        return codesByField;
    },
    {
        eligibleRules: [],
        levels: new Set(),
        programs: new Set(),
        locations: new Set(),
        majors: new Set(),
        minors: new Set(),
        concentrations: new Set(),
        majorDescriptions: [],
    });
    const valid = !!eligibleRules.length;
    // If ANY eligible rule is missing code(s) for the field in question (e.g. campus),
    // OR there is NO selected value for that field on the application,
    // then use the fall back values. Otherwise, filter based on the rules.
    const {
        levels, programs, locations,
        majors, minors, majorDescriptions
    } = eligibleRules.reduce((codesByField, rule) => {
        // Use fallback values if one is selected
        // There should be a value in this field on every rule.
        if (selectedLevel) {
            codesByField.levels = null;
        } else if (codesByField.levels && rule.levelCode) {
            codesByField.levels.add(rule.levelCode);
        }

        if (selectedProgram) {
            codesByField.programs = null;
        } else if (codesByField.programs && rule.programCode) {
            codesByField.programs.add(rule.programCode);
        }

        // Use fallback values if one is selected
        // OR if there is no value for this field.
        // No value means ANY that match level are supported.
        if (selectedLocation || !rule.campusCode) {
            codesByField.locations = null;
        } else if (codesByField.locations) {
            codesByField.locations.add(rule.campusCode);
        }

        const termFilteredMajors2 = filterByTermRange(rule.majorsDepartments, selectedTerm);
        const termFilteredMinors2 = filterByTermRange(rule.minors, selectedTerm);

        if (selectedMajor || !termFilteredMajors2?.length) {
            codesByField.majors = null;
            codesByField.majorDescriptions = null;
        } else if (codesByField.majors) {
            termFilteredMajors2.forEach(md => {
                if (md.majorCode) {
                    codesByField.majors.add(md.majorCode);

                    if (
                        md.webDescription && md.webDisplayInd &&
                        codesByField.majorDescriptions
                    ) {
                        codesByField.majorDescriptions.push({
                            major: md.majorCode,
                            program: rule.programCode,
                            label: md.webDescription,
                            value: md.majorRule
                        });
                    }
                }
            });
        }

        if (selectedMinor || !termFilteredMinors2?.length) {
            codesByField.minors = null;
        } else if (codesByField.minors) {
            termFilteredMinors2.forEach(m => {
                if (m.minorCode) {
                    codesByField.minors.add(m.minorCode);
                }
            });
        }

        return codesByField;
    },
    {
        programs: new Set(),
        levels: new Set(),
        locations: new Set(),
        majors: new Set(),
        minors: new Set(),
        majorDescriptions: [],
    });

    return {
        valid,
        programs: Array.from(programs || fallbackValues.programs),
        levels: Array.from(levels || fallbackValues.levels),
        locations: Array.from(locations || fallbackValues.locations || []),
        majors: Array.from(majors || fallbackValues.majors || []),
        minors: Array.from(minors || fallbackValues.minors || []),
        // fallback values should suffice for concentrations.
        // allow all valid for level/program/major rule.
        concentrations: Array.from(fallbackValues.concentrations || []),
        majorDescriptions: majorDescriptions || fallbackValues.majorDescriptions
    };
};
